import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Plus, Database, CheckCircle, AlertCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [schoolName, setSchoolName] = useState("");
  const [schoolContact, setSchoolContact] = useState("");
  const [dbStatus, setDbStatus] = useState(null);
  const [schoolStats, setSchoolStats] = useState({}); // { school_name: count }

  const load = useCallback(() =>
    Promise.all([api.get("/admin/stats"), api.get("/schools"), api.get("/teachers"), api.get("/sessions"), api.get("/admin/db-status"), api.get("/admin/school-stats")]).then(
      ([s, sc, t, se, db, ss]) => {
        setStats(s.data);
        setSchools(sc.data);
        setTeachers(t.data);
        setSessions(se.data);
        setDbStatus(db.data);
        setSchoolStats(ss.data);
      }
    ), []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        if (r.data.role !== "admin") throw new Error("not admin");
        setUser(r.data);
        return load();
      })
      .catch(() => navigate("/login"));
  }, [navigate, load]);

  if (!user) return null;

  const addSchool = async (e) => {
    e.preventDefault();
    try {
      await api.post("/schools", { name: schoolName, contact: schoolContact });
      setSchoolName("");
      setSchoolContact("");
      toast.success("School added");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeSchool = async (id) => {
    await api.delete(`/schools/${id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  const statCards = [
    { label: "Teachers", value: stats?.teachers, testId: "stat-teachers" },
    { label: "Schools", value: stats?.schools, testId: "stat-schools" },
    { label: "Sessions", value: stats?.sessions, testId: "stat-sessions" },
    { label: "Transcribed", value: stats?.transcribed, testId: "stat-transcribed" },
  ];

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#FDF3EC]">
      <TopBar user={user} />
      <div className="mx-auto w-full max-w-5xl px-5 py-10 md:px-8">
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="admin-title"
          className="font-display text-3xl font-bold text-[#1A1918] md:text-4xl"
        >
          Admin panel
        </motion.h1>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {statCards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={c.testId}
              className="rounded-2xl border border-black/5 bg-white p-5"
            >
              <p className="font-display text-3xl font-bold text-[#E8542A]">{c.value ?? "—"}</p>
              <p className="mt-1 text-xs text-[#8A6D5C]">{c.label}</p>
            </motion.div>
          ))}
        </div>

        {/* DB Status */}
        {dbStatus && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 rounded-2xl border border-black/5 bg-white p-5"
          >
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-[#8A6D5C]" />
              <h2 className="text-sm font-bold text-[#1A1918]">Database connection</h2>
              {dbStatus.status === "connected" ? (
                <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-emerald-600">
                  <CheckCircle className="h-3.5 w-3.5" /> Connected
                </span>
              ) : (
                <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-red-500">
                  <AlertCircle className="h-3.5 w-3.5" /> Error
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#8A6D5C]">
              <span><strong className="text-[#1A1918]">Mode:</strong> {dbStatus.connection}</span>
              <span><strong className="text-[#1A1918]">DB:</strong> {dbStatus.db_name}</span>
              {!dbStatus.using_shared_db && (
                <span className="text-amber-600">
                  To share mobile app data: allowlist <strong>{dbStatus.app_ip}</strong> in Atlas Network Access, then set CLASSBRIDGE_MONGO_URL + CLASSBRIDGE_DB_NAME and restart backend.
                </span>
              )}
            </div>
          </motion.section>
        )}

        {/* Schools */}
        <section className="mt-8 rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="text-sm font-bold text-[#1A1918]">Schools</h2>
          <form onSubmit={addSchool} className="mt-4 flex flex-wrap gap-3">
            <Input data-testid="school-name-input" placeholder="School name" value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)} required className="max-w-xs rounded-xl border-[#E8D5C8]" />
            <Input data-testid="school-contact-input" placeholder="Contact (optional)" value={schoolContact}
              onChange={(e) => setSchoolContact(e.target.value)} className="max-w-xs rounded-xl border-[#E8D5C8]" />
            <Button type="submit" data-testid="add-school-btn"
              className="rounded-xl bg-[#E8542A] text-white hover:bg-[#C94520]">
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
          </form>
          <div className="mt-4 space-y-2">
            {schools.map((s) => (
              <div key={s.id} data-testid={`school-row-${s.id}`}
                className="flex items-center justify-between rounded-xl bg-[#FDF3EC] px-4 py-3 text-sm">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="font-medium text-[#1A1918]">{s.name}</span>
                  {schoolStats[s.name] > 0 && (
                    <span
                      data-testid={`school-sessions-badge-${s.id}`}
                      className="rounded-full bg-[#E8542A]/10 px-2.5 py-0.5 text-xs font-bold text-[#E8542A]"
                    >
                      {schoolStats[s.name]} this month
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#8A6D5C]">{s.contact}</span>
                  <button data-testid={`delete-school-${s.id}`} onClick={() => removeSchool(s.id)}
                    className="text-red-400 transition-colors hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {schools.length === 0 && <p className="text-sm text-[#8A6D5C]">No schools yet.</p>}
          </div>
        </section>

        {/* Teachers */}
        <section className="mt-6 rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="text-sm font-bold text-[#1A1918]">Teachers</h2>
          <div className="mt-4 space-y-2">
            {teachers.map((t) => (
              <div key={t.id} data-testid={`teacher-row-${t.id}`}
                className="flex items-center justify-between rounded-xl bg-[#FDF3EC] px-4 py-3 text-sm">
                <span className="font-medium text-[#1A1918]">{t.name}</span>
                <span className="text-xs text-[#8A6D5C]">{t.mobile}</span>
              </div>
            ))}
            {teachers.length === 0 && <p className="text-sm text-[#8A6D5C]">No teachers yet.</p>}
          </div>
        </section>

        {/* Sessions */}
        <section className="mt-6 rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="text-sm font-bold text-[#1A1918]">Recent sessions</h2>
          <div className="mt-4 space-y-2">
            {sessions.slice(0, 20).map((s) => (
              <div key={s.id} data-testid={`admin-session-row-${s.id}`}
                className="flex items-center justify-between rounded-xl bg-[#FDF3EC] px-4 py-3 text-sm">
                <span className="font-medium text-[#1A1918]">
                  {s.subject || "Class"}{s.standard ? ` · Std ${s.standard}` : ""}{s.division ? ` ${s.division}` : ""}
                </span>
                <span className="text-xs text-[#8A6D5C]">
                  {s.teacher_name} · {new Date(s.created_at).toLocaleDateString("en-IN")}
                </span>
              </div>
            ))}
            {sessions.length === 0 && <p className="text-sm text-[#8A6D5C]">No sessions yet.</p>}
          </div>
        </section>
      </div>
    </main>
  );
}


